import { useQuery } from "@tanstack/react-query";
import { Button, message, Spin, Switch } from "antd";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { useState } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  exportInsightsAwardedRedeemedOverTimeByOutlet,
  rewardsAwardedRedeemedOverTimeByOutlet,
} from "../../actions/acc_actions";
import Loading from "../../utils/Loading";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
);

const RewardsByOutletChart = ({ dateFrom, dateTo, selectedSchema }) => {
  const { t } = useTranslation();
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const [exportLoading, setExportLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { isLoading, data } = useQuery({
    queryKey: [
      "insightsAwardedRedeemedByOutlet",
      businessSlug,
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      rewardsAwardedRedeemedOverTimeByOutlet({
        to: dateTo,
        from: dateFrom,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!businessSlug && !!selectedSchema,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const handleExport = async () => {
    if (!businessSlug || !dateFrom || !dateTo) return;
    setExportLoading(true);

    const res = await exportInsightsAwardedRedeemedOverTimeByOutlet({
      to: dateTo,
      from: dateFrom,
      loyalty_scheme_slug: selectedSchema,
    });

    setExportLoading(false);
    if (!res.success) message.error(t("export_failed"));
    else message.success(t("export_success"));
  };

  if (isLoading) return <Loading />;

  const chartData = data?.body?.data;
  if (!chartData || chartData.length === 0)
    return (
      <div>
        <h3>{t("rewards_redeemed_per_outlet")}</h3>
        <Button disabled>{t("export_csv")}</Button>
      </div>
    );

  // ✅ Group by outlet
  const grouped = {};
  chartData.forEach((item) => {
    if (!grouped[item.outlet_redeemed]) grouped[item.outlet_redeemed] = [];
    grouped[item.outlet_redeemed].push({
      date: item.date,
      value: item.rewards_redeemed,
    });
  });

  const allOutlets = Object.keys(grouped);

  // ✅ Determine Top 10 by total rewards
  const sortedOutlets = allOutlets
    .map((o) => ({
      outlet: o,
      total: grouped[o].reduce((sum, x) => sum + x.value, 0),
    }))
    .sort((a, b) => b.total - a.total);

  const visibleOutlets = showAll
    ? sortedOutlets.map((x) => x.outlet)
    : sortedOutlets.slice(0, 10).map((x) => x.outlet);

  const allDates = [...new Set(chartData.map((i) => i.date))];

  // ✅ Color palette
  const colors = [
    "#007AFF",
    "#FF3B30",
    "#34C759",
    "#FF9500",
    "#AF52DE",
    "#5AC8FA",
    "#FF2D55",
    "#5856D6",
    "#FFCC00",
    "#30D158",
    "#BF5AF2",
    "#64D2FF",
    "#FF375F",
    "#0A84FF",
    "#FFD60A",
  ];

  const datasets = visibleOutlets.map((outlet, index) => {
    const outletDataByDate = allDates.map((date) => {
      const found = grouped[outlet].find((d) => d.date === date);
      return found ? found.value : 0;
    });

    return {
      label: outlet,
      data: outletDataByDate,
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length] + "33",
      borderWidth: 2,
      tension: 0.35,
    };
  });

  const chartConfig = { labels: allDates, datasets };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "day", displayFormats: { day: "MMM dd" } },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: t("rewards_redeemed") },
      },
    },
  };

  return (
    <div className="chart">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h3>{t("rewards_redeemed_per_outlet")}</h3>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span>{t("show_all_outlets")}</span>
          <Switch checked={showAll} onChange={setShowAll} />

          <Button
            type="primary"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? <Spin size="small" /> : t("download_data")}
          </Button>
        </div>
      </div>

      <div
        className="chart-container"
        style={{ height: "700px", maxHeight: "85vh" }}
      >
        <Line data={chartConfig} options={chartOptions} />
      </div>
    </div>
  );
};

export default RewardsByOutletChart;
